import React, { useRef } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { AtlassianIcon } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { ColumnLayout } from '@/libraries/design/foundations/grid/src/column-layout';
import wacToken from '@/libraries/design/foundations/token/src/wac-token';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { GlobalNavProvider } from './nav-links-container';
import SearchAndProfile from './nav-search-and-profile';
import { SkipToContent } from './skip-to-content';

export type SimplifiedProductSubNavProps = {
	readonly productLogo: JSX.Element;
	readonly productLogoUrl: string;
	readonly productLogoAriaLabel: string;
	readonly productKey?: ProductKeyTypes;
	readonly isShowTryNowLink?: boolean;
	readonly isShowUserProfile?: boolean;
	readonly navigationTrynowCtaText?: string;
	readonly navigationTrynowCtaUrl?: string;
	readonly navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
	readonly navigationTrynowCtaButtonVariant?: 'primary' | 'secondary';
};

const simplifiedProductPageSubNavStyle = css({
	position: 'sticky',
	insetBlockStart: 0,
	zIndex: 5,
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	width: '100%',
	minHeight: token('space.800'),
	backgroundColor: wacToken('color.neutral.light0'),

	// This is a workaround until this nav gets replaced for AI SDLC page (/software/jira/dev) to hide the username on small screens
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.avatar-button span[aria-hidden="true"]': {
		[media.below.sm]: {
			display: 'none',
		},
	},
});

const simplifiedProductPageSubNavStyleLoggedOut = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'button[name="sign-in"]': {
		[media.below.sm]: {
			display: 'none',
		},
	},
});

const simplifiedSubNavAtlasIconContainerStyle = css({
	display: 'none',

	[media.above.sm]: {
		height: '68px',
		width: '68px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		backgroundColor: wacToken('color.extended.blue700'),
	},
});

const simplifiedSubNavAtlasIconStyle = css({
	height: '56px',
	width: '56px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const simplifiedSubNavContentWrapperStyle = css({
	flex: 1,
	minWidth: 0,
	zIndex: -1,
	[media.above.sm]: {
		transform: 'translateX(-34px)',
	},
});

const simplifiedProductPageSubNavDarkThemeStyle = css({
	backgroundColor: wacToken('color.neutral.dark100'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"button[name='sign-in'] span": {
		color: wacToken('color.neutral.light0'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.avatar-button span': {
		color: wacToken('color.neutral.light0'),
	},
});

const simplifiedProductPageSubNavContentStyle = css({
	boxSizing: 'border-box',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.300'),
	width: '100%',
	minHeight: token('space.800'),
	marginInline: 'auto',
	[media.below.sm]: {
		gap: token('space.150'),
	},
});

const simplifiedProductPageSubNavLogoLinkStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	minWidth: 0,
	textDecoration: 'none',
	[media.below.sm]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			maxHeight: '24px',
			height: '24px',
		},
	},
});

const simplifiedProductPageSubNavActionsStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	flexShrink: 0,
	visibility: 'hidden',
});

export const SimplifiedProductSubNav = ({
	productLogo,
	productLogoUrl,
	productLogoAriaLabel,
	productKey,
	isShowTryNowLink = true,
	isShowUserProfile = true,
	navigationTrynowCtaText,
	navigationTrynowCtaUrl,
	navigationTrynowCtaOnClick,
	navigationTrynowCtaButtonVariant = 'primary',
}: SimplifiedProductSubNavProps) => {
	const intl = useIntl();
	const { isDarkTheme } = useDarkTheme();
	const [, setIsOpenSearch] = React.useState(false);
	const searchAndProfileRef = useRef<HTMLDivElement | null>(null);

	const defaultCtaText = intl.formatMessage({
		id: 'productpagesubnav.try-now-button-label',
		defaultMessage: 'Get it free',
	});

	const { isUserLoggedIn, requestInProgress } = useLoggedInUser();

	const defaultCtaUrl = useLocalizedHref(
		'/try/cloud/signup?application=wac&signupSource=jira-ai-native',
	);

	const themedProductLogo =
		React.isValidElement<{ lightMode?: boolean }>(productLogo) &&
		typeof productLogo.type !== 'string'
			? React.cloneElement(productLogo, { lightMode: isDarkTheme })
			: productLogo;

	return (
		<GlobalNavProvider globalNavItemKeys={[]}>
			<header
				className="simplified-product-nav-container"
				css={[
					simplifiedProductPageSubNavStyle,
					!isUserLoggedIn && simplifiedProductPageSubNavStyleLoggedOut,
					isDarkTheme && simplifiedProductPageSubNavDarkThemeStyle,
				]}
				data-testid="simplified-product-subnav"
			>
				<a
					css={simplifiedSubNavAtlasIconContainerStyle}
					href={useLocalizedHref('/')}
					aria-label={intl.formatMessage({
						id: 'simplifiedProductSubNav.atlassian-logo-label',
						defaultMessage: 'Atlassian home',
					})}
					data-event="clicked"
					data-event-component="linkButton"
					data-event-container="productPageNav"
					data-product-key={productKey}
				>
					<span css={simplifiedSubNavAtlasIconStyle}>
						<AtlassianIcon size="medium" appearance="inverse" />
					</span>
				</a>
				<div css={simplifiedSubNavContentWrapperStyle}>
					<ColumnLayout layout="12">
						<nav
							aria-label={intl.formatMessage({
								id: 'productPageNav.main-nav-label',
								defaultMessage: 'Main',
							})}
							css={simplifiedProductPageSubNavContentStyle}
						>
							<SkipToContent />
							<a
								className="navigation-bar-logo-link"
								css={simplifiedProductPageSubNavLogoLinkStyle}
								href={productLogoUrl}
								aria-label={productLogoAriaLabel}
								data-event="clicked"
								data-event-component="linkButton"
								data-event-container="productPageNav"
								data-product-key={productKey}
							>
								{themedProductLogo}
							</a>
							<div
								css={[
									simplifiedProductPageSubNavActionsStyle,
									!requestInProgress && css({ visibility: 'visible' }),
								]}
							>
								<SearchAndProfile
									isShowTryNowLink={isShowTryNowLink || (!requestInProgress && !isUserLoggedIn)}
									productKey={productKey}
									isShowSearchButton={false}
									setIsOpenSearch={setIsOpenSearch}
									navigationTrynowCtaText={navigationTrynowCtaText ?? defaultCtaText}
									navigationTrynowCtaUrl={navigationTrynowCtaUrl ?? defaultCtaUrl}
									navigationTrynowCtaOnClick={navigationTrynowCtaOnClick}
									navigationTrynowCtaButtonVariant={navigationTrynowCtaButtonVariant}
									isDisplayDivider={false}
									searchAndProfileRef={searchAndProfileRef}
									isShowAppSwitcher={false}
									isShowUserProfile={isShowUserProfile}
									navType="productNav"
								/>
							</div>
						</nav>
					</ColumnLayout>
				</div>
			</header>
		</GlobalNavProvider>
	);
};
